import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppNavigate } from "../lib/route-primitives";
import { useQuery } from "@tanstack/react-query";
import { adminUsersApi, adminPlansApi } from "../lib/api";
import { Loader2, Users, Search, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { getPlanColors } from "../lib/plan-colors";

interface OrganizerRow {
  id: string;
  email: string;
  display_name: string | null;
  phone_e164: string | null;
  plan: string;
  created_at: string;
  updated_at: string;
  wedding_count: number;
}

const USER_LIMIT = 50;

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap",
      getPlanColors(plan).badge
    )}>
      {plan}
    </span>
  );
}

function Avatar({ name, email }: { name: string | null; email: string }) {
  const initial = (name ?? email)[0].toUpperCase();
  return (
    <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
      {initial}
    </div>
  );
}

interface PlanRow { id: string; slug: string; label: string; sort_order: number; }

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();

  const [users, setUsers]         = useState<OrganizerRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]     = useState(false);
  const [search, setSearch]       = useState("");
  const [planFilter, setPlanFilter] = useState(() => searchParams.get("plan") ?? "");

  const { data: plansData } = useQuery({
    queryKey: ["admin", "plans"],
    queryFn: () => adminPlansApi.get<PlanRow[]>("/"),
  });
  const plans = (plansData?.data ?? []).sort((a, b) => a.sort_order - b.sort_order);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminUsersApi.get<OrganizerRow[]>(`?limit=${USER_LIMIT}`);
      if (res.ok && res.data) {
        setUsers(res.data);
        setHasMore(res.data.length >= USER_LIMIT);
      }
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMoreLoadingRef = useRef(false);
  const fetchMore = useCallback(async () => {
    if (fetchMoreLoadingRef.current || !users.length) return;
    fetchMoreLoadingRef.current = true;
    setLoadingMore(true);
    try {
      const cursor = users[users.length - 1].created_at;
      const res = await adminUsersApi.get<OrganizerRow[]>(
        `?limit=${USER_LIMIT}&cursor=${encodeURIComponent(cursor)}`,
      );
      const rows = res.data ?? [];
      setUsers((prev) => [...prev, ...rows]);
      setHasMore(rows.length >= USER_LIMIT);
    } catch {
      // ignore
    } finally {
      fetchMoreLoadingRef.current = false;
      setLoadingMore(false);
    }
  }, [users]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const visible = useMemo(() => {
    let list = users;
    if (planFilter) list = list.filter((u) => u.plan === planFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.display_name ?? "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, planFilter, search]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="font-serif italic text-3xl text-foreground">{t("admin.usersTitle")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("admin.usersDesc")}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="İsim veya e-posta ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm bg-muted/40 border border-primary/20 rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary transition-[border-color,box-shadow] placeholder:text-muted-foreground/50"
          />
        </div>

        {/* Plan pill group */}
        {plans.length > 0 && (
          <div className="flex items-center rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setPlanFilter("")}
              className={cn("px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer", planFilter === "" ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}
            >
              {t("common.all")}
            </button>
            {plans.map((p) => (
              <button
                key={p.slug}
                onClick={() => setPlanFilter(p.slug)}
                className={cn("px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer", planFilter === p.slug ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        {!isLoading && (
          <span className="text-xs text-muted-foreground ml-auto">
            {visible.length} kullanıcı{hasMore ? "+" : ""}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
          <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">{t("admin.usersEmpty")}</p>
        </div>
      ) : (
        <>
          {/* ── Desktop table ───────────────────────────────────── */}
          <div className="hidden md:block rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Organizatör</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">E-posta</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground w-24">Plan</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-center">Düğün</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Kayıt</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visible.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => navigate(paths.userDetail(u.id))}
                      className="hover:bg-primary/5 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.display_name} email={u.email} />
                          <span className="text-sm font-medium text-foreground">
                            {u.display_name ?? <span className="text-muted-foreground/60 italic">İsimsiz</span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground">{u.email}</td>
                      <td className="px-5 py-4 w-24">
                        <PlanBadge plan={u.plan} />
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground text-center">{u.wedding_count}</td>
                      <td className="px-5 py-4 text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(u.created_at).toLocaleDateString(i18n.language)}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <ChevronRight className="h-4 w-4 text-muted-foreground inline-block" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Mobile cards ────────────────────────────────────── */}
          <div className="md:hidden space-y-2">
            {visible.map((u) => (
              <div
                key={u.id}
                onClick={() => navigate(paths.userDetail(u.id))}
                className="flex items-center gap-3 bg-card border border-border rounded-2xl px-4 py-3.5 hover:bg-primary/5 transition-colors cursor-pointer"
              >
                <Avatar name={u.display_name} email={u.email} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground truncate">
                      {u.display_name ?? u.email}
                    </p>
                    <PlanBadge plan={u.plan} />
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{u.email}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </div>
            ))}
          </div>
        </>
      )}

      {hasMore && !isLoading && (
        <div className="flex justify-center pt-2">
          <button
            onClick={fetchMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 rounded-lg border border-primary/20 px-5 py-2.5 text-sm font-medium text-foreground hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            {loadingMore ? t("common.loading") : t("common.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}
